import { type AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useAxiosAuth from "../hooks/useAxiosAuth.ts";
import { siteGeometryQueryKey } from "./useSiteGeometry.ts";
import {
  patchSiteGeometryLink,
  writeGeometryQueryCache,
} from "./refreshSiteAndGeometryCaches.ts";

export interface SaveSiteGeometryPayload {
  id?: number | string;
  site: string;
  siteId?: number | string;
  poly: string;
  isExistingSynthetic: boolean;
}

const extractSiteIdFromUrl = (siteUrl: string) =>
  siteUrl.match(/\/sites\/(\d+)\//)?.[1];

const saveSiteGeometry = async (
  axiosAuth: AxiosInstance,
  { id, site, poly, isExistingSynthetic }: SaveSiteGeometryPayload
) => {
  const baseUrl = `${process.env.REACT_APP_API_BASE_URL}site_geometry/`;

  if (isExistingSynthetic || !id) {
    const response = await axiosAuth.post(baseUrl, { site, poly });
    return response.data;
  }

  const response = await axiosAuth.patch(`${baseUrl}${id}/`, {
    poly,
    synthetic: false,
  });
  return response.data;
};

export function useEditSiteGeometry() {
  const axiosAuth = useAxiosAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (geometryData: SaveSiteGeometryPayload) =>
      saveSiteGeometry(axiosAuth, geometryData),
    onSuccess: (savedGeometry, variables) => {
      const siteId =
        variables.siteId != null
          ? String(variables.siteId)
          : extractSiteIdFromUrl(variables.site);

      if (variables.isExistingSynthetic && variables.id != null) {
        queryClient.removeQueries({
          queryKey: siteGeometryQueryKey(variables.id),
        });
      }

      if (savedGeometry && typeof savedGeometry === "object") {
        writeGeometryQueryCache(
          queryClient,
          savedGeometry as Record<string, unknown>
        );
        if (siteId && typeof savedGeometry.url === "string") {
          patchSiteGeometryLink(queryClient, siteId, savedGeometry.url);
        }
      }

      queryClient.invalidateQueries({ queryKey: ["portfolioSites"] });
      queryClient.invalidateQueries({ queryKey: ["sites"] });
    },
    onError: (error) => {
      console.error("saveSiteGeometry mutation failed", error);
    },
  });
}

